/**
 * What the settings view models share: their platform services (`SettingsDeps`) and the photo pipeline used for
 * profile photos and workspace icons — pick → crop to a square → 512×512 WebP (the sticker image processor) → the
 * regular upload queue (`DataSource.uploadFile`, resumable and offline-tolerant) → the view model attaches the stored
 * file (`avatarFileId` / `iconFileId`).
 */
import type { MessageDescriptor } from "../i18n.ts";
import { msg } from "../i18n.ts";
import { uploadProgress } from "../logic/files.ts";
import { errorKind, errorMessage, type CropArea, type PickedFile, type SettingsApi, type StickerImageProcessor, type TimeZones, type UploadTarget } from "../ports.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Platform services for settings (web: platform/settings; iOS/Android: hand-written adapters). */
export type SettingsDeps = {
  api: SettingsApi;
  zones: TimeZones;
  /** Square crop + 512×512 WebP (the same processor stickers use). */
  images: Pick<StickerImageProcessor, "inspect" | "render" | "release">;
};

export type PhotoPhase = "idle" | "cropping" | "uploading" | "failed";

export type PhotoState = {
  phase: PhotoPhase;
  /** Cropping: what the cropper shows (a local preview of the picked file). */
  source?: string;
  /** Uploading: the new picture from local bytes (`local-upload:<id>`). */
  preview?: string;
  /** Uploading: 0…1. */
  progress?: number;
  /** Failed: why. */
  error?: MessageDescriptor;
};

export const IDLE_PHOTO: PhotoState = { phase: "idle" };

/**
 * One photo at a time: `pick` → `setCrop` → `confirm` (render, upload, then `attach(fileId)` from the owner) or
 * `cancel`. Reports every state change to `onChange`.
 */
export class PhotoPipeline {
  private file: PickedFile | undefined;
  private crop: CropArea | undefined;
  private source: string | undefined;
  private generation = 0;
  private unsubscribe: (() => void) | undefined;
  state: PhotoState = IDLE_PHOTO;

  constructor(
    private readonly ws: WorkspaceViewModel,
    private readonly images: SettingsDeps["images"],
    private readonly target: UploadTarget,
    private readonly attach: (fileId: string) => Promise<void>,
    private readonly onChange: (s: PhotoState) => void,
  ) {}

  private set(s: PhotoState): void {
    this.state = s;
    this.onChange(s);
  }

  private releaseSource(): void {
    if (this.source !== undefined) this.images.release(this.source);
    this.source = undefined;
  }

  /** A picked image: opens the cropper (centred square until the cropper reports one). */
  async pick(file: PickedFile): Promise<void> {
    const gen = ++this.generation;
    this.releaseSource();
    this.file = file;
    this.crop = undefined;
    try {
      const info = await this.images.inspect(file);
      if (gen !== this.generation) {
        this.images.release(info.previewUrl);
        return;
      }
      this.source = info.previewUrl;
      this.set({ phase: "cropping", source: info.previewUrl });
    } catch {
      if (gen === this.generation) this.set({ phase: "failed", error: msg("profile.photo.unreadable") });
    }
  }

  setCrop(rect: CropArea): void {
    this.crop = rect;
  }

  /** Makes the 512×512 image, uploads it and lets the owner attach it. Resolves true when attached. */
  async confirm(): Promise<boolean> {
    const file = this.file;
    if (file === undefined || this.state.phase !== "cropping") return false;
    const gen = ++this.generation;
    this.set({ phase: "uploading", progress: 0 });
    try {
      const out = await this.images.render(file, this.crop === undefined ? { removeBackground: false } : { crop: this.crop, removeBackground: false });
      if (gen !== this.generation) return false;
      const upload = this.ws.deps.data.uploadFile(this.ws.wsId, out, this.target);
      const uploads = this.ws.deps.data.uploads(this.ws.wsId);
      const follow = () => {
        const entry = uploads.getSnapshot().find((u) => u.id === upload.id);
        if (entry === undefined || gen !== this.generation || this.state.phase !== "uploading") return;
        const next: PhotoState = { phase: "uploading", progress: uploadProgress(entry.state) };
        if (entry.previewUrl !== undefined) next.preview = entry.previewUrl;
        if (next.progress !== this.state.progress || next.preview !== this.state.preview) this.set(next);
      };
      this.unsubscribe?.();
      this.unsubscribe = uploads.subscribe(follow);
      follow();
      const stored = await upload.done;
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      if (gen !== this.generation) return false;
      await this.attach(stored.id);
      if (gen !== this.generation) return true;
      this.releaseSource();
      this.file = undefined;
      this.set(IDLE_PHOTO);
      return true;
    } catch (e) {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      if (gen !== this.generation) return false;
      const error = errorKind(e) === "network" ? msg("settings.offline") : errorMessage(e).startsWith("stickers.") ? msg("profile.photo.unreadable") : msg("profile.photo.failed");
      this.set({ phase: "failed", error });
      return false;
    }
  }

  cancel(): void {
    this.generation += 1;
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.releaseSource();
    this.file = undefined;
    this.set(IDLE_PHOTO);
  }

  dispose(): void {
    this.generation += 1;
    this.unsubscribe?.();
    this.releaseSource();
  }
}
